import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { formatPrice } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function CartDrawer() {
    const { cart, isOpen, setIsOpen, updateQty } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    if (!mounted) return null;

    const goToCheckout = () => {
        setIsOpen(false);
        if (!user) {
            navigate("/login?next=/checkout");
        } else {
            navigate("/checkout");
        }
    };

    return (
        <>
            {/* Overlay */}
            <div
                onClick={() => setIsOpen(false)}
                data-testid="cart-overlay"
                className={`fixed inset-0 z-50 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
                    isOpen ? "opacity-100" : "pointer-events-none opacity-0"
                }`}
            />
            {/* Drawer */}
            <aside
                data-testid="cart-drawer"
                className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-border-dim bg-obsidian transition-transform duration-500 ease-out ${
                    isOpen ? "translate-x-0" : "translate-x-full"
                }`}
            >
                <div className="flex items-center justify-between border-b border-border-dim px-6 py-5">
                    <h3 className="font-display text-2xl text-gold">Your Bag</h3>
                    <button
                        onClick={() => setIsOpen(false)}
                        data-testid="cart-close"
                        className="text-white/70 hover:text-gold"
                        aria-label="Close cart"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {cart.items.length === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
                        <p className="font-display text-3xl text-white/80">A little space, waiting.</p>
                        <p className="mt-3 text-sm text-white/50">
                            Your bag is empty. Begin where you'd like.
                        </p>
                        <Link
                            to="/shop"
                            onClick={() => setIsOpen(false)}
                            data-testid="cart-shop-cta"
                            className="mt-8 inline-flex items-center justify-center bg-gold px-8 py-3 text-[12px] uppercase tracking-[0.25em] text-obsidian transition-colors hover:bg-gold-light"
                        >
                            Discover
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="flex-1 overflow-y-auto px-6 py-4">
                            {cart.items.map((it) => (
                                <div
                                    key={`${it.product_id}-${it.size || ""}-${it.color || ""}`}
                                    data-testid={`cart-item-${it.product_id}`}
                                    className="flex gap-4 border-b border-border-dim py-5"
                                >
                                    <div className="h-24 w-20 flex-shrink-0 overflow-hidden bg-surface">
                                        <img
                                            src={it.product.images?.[0]}
                                            alt={it.product.name}
                                            className="h-full w-full object-cover"
                                        />
                                    </div>
                                    <div className="flex flex-1 flex-col">
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <p className="font-display text-lg leading-tight text-white">
                                                    {it.product.name}
                                                </p>
                                                <p className="mt-1 text-xs text-white/40">
                                                    {[it.size, it.color].filter(Boolean).join(" / ")}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => updateQty(it.product_id, 0, it.size, it.color)}
                                                data-testid={`cart-remove-${it.product_id}`}
                                                className="text-white/40 hover:text-heart"
                                                aria-label="Remove"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                        <div className="mt-auto flex items-center justify-between">
                                            <div className="inline-flex items-center border border-border-dim">
                                                <button
                                                    onClick={() => updateQty(it.product_id, it.quantity - 1, it.size, it.color)}
                                                    data-testid={`cart-dec-${it.product_id}`}
                                                    className="px-2 py-1 text-white/70 hover:text-gold"
                                                >
                                                    <Minus className="h-3 w-3" />
                                                </button>
                                                <span className="min-w-[2rem] text-center text-sm">{it.quantity}</span>
                                                <button
                                                    onClick={() => updateQty(it.product_id, it.quantity + 1, it.size, it.color)}
                                                    data-testid={`cart-inc-${it.product_id}`}
                                                    className="px-2 py-1 text-white/70 hover:text-gold"
                                                >
                                                    <Plus className="h-3 w-3" />
                                                </button>
                                            </div>
                                            <p className="text-sm text-gold">{formatPrice(it.line_total)}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-border-dim px-6 py-6">
                            <div className="flex items-baseline justify-between">
                                <span className="text-[11px] uppercase tracking-[0.25em] text-white/50">Subtotal</span>
                                <span data-testid="cart-subtotal" className="font-display text-3xl text-gold">
                                    {formatPrice(cart.subtotal)}
                                </span>
                            </div>
                            <p className="mt-1 text-xs text-white/40">
                                Shipping & taxes calculated at checkout.
                            </p>
                            <button
                                onClick={goToCheckout}
                                data-testid="cart-checkout-btn"
                                className="mt-6 w-full bg-gold py-4 text-[12px] uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-gold-light"
                            >
                                Checkout
                            </button>
                            <Link
                                to="/shop"
                                onClick={() => setIsOpen(false)}
                                className="mt-3 inline-block w-full border border-border-dim py-3 text-center text-[12px] uppercase tracking-[0.25em] text-white/70 transition-colors hover:border-gold hover:text-gold"
                            >
                                Keep browsing
                            </Link>
                        </div>
                    </>
                )}
            </aside>
        </>
    );
}
